import type { FC } from "react";
import { Navbar, NavbarBrand, Button } from "flowbite-react";
import { useAuth } from "../contexts/AuthContext";
import { HiUser, HiLogout, HiMenuAlt1, HiMoon, HiSun } from "react-icons/hi";
import { useThemeMode } from "../hooks/useThemeMode";
import { Link } from "react-router-dom";

interface ExampleNavbarProps {
  onToggleSidebar?: () => void;
}

const ExampleNavbar: FC<ExampleNavbarProps> = function ({ onToggleSidebar }) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useThemeMode();

  const handleLogout = async () => {
    try {
      console.log("Starting sign out process");
      await signOut();
      console.log("Sign out completed, redirecting...");

      // Add a small delay to ensure state is properly cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Force a complete page reload and redirect to ensure clean state
      window.location.replace("/authentication/sign-in");
    } catch (error) {
      console.error("Error during sign out:", error);

      // Even if signOut fails, still try to clear local state and redirect
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (storageError) {
        console.warn(
          "Error clearing storage after failed logout:",
          storageError
        );
      }

      // Still redirect to login page
      window.location.replace("/authentication/sign-in");
    }
  };
  return (
    <Navbar fluid className="fixed top-0 z-50 w-full border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="w-full p-2 md:p-3 lg:px-5 lg:pl-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <Button
              color="gray"
              onClick={onToggleSidebar}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 md:h-10 md:w-10"
            >
              <span className="sr-only">Toggle sidebar</span>
              <HiMenuAlt1 className="h-5 w-5 md:h-6 md:w-6" />
            </Button>
            <NavbarBrand href="/">
              <img
                alt=""
                src="/images/logo.webp"
                className="mr-2 h-7 md:mr-3 md:h-8"
              />
              <span className="self-center whitespace-nowrap text-lg font-semibold dark:text-white md:text-xl lg:text-2xl">
                D2D Boom
              </span>
            </NavbarBrand>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <div className="flex items-center gap-2 md:gap-3">
              <Link
                to="/profile"
                className="flex items-center gap-2 focus:outline-none md:gap-3"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 md:h-8 md:w-8">
                  <HiUser className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </div>
                {user ? (
                  <span className="hidden text-sm font-medium text-gray-600 dark:text-gray-300 md:block">
                    {user.email}
                  </span>
                ) : null}
              </Link>
              {user ? (
                <Button
                  size="sm"
                  color="gray"
                  onClick={handleLogout}
                  className="h-8 w-8 p-0 md:h-8 md:w-auto md:px-3"
                >
                  <HiLogout className="h-4 w-4" />
                </Button>
              ) : (
                <a
                  href="/authentication/sign-in"
                  className="hidden cursor-pointer text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-400 md:block"
                >
                  Please log in
                </a>
              )}
            </div>
            <Button
              color="gray"
              onClick={toggleTheme}
              className="h-8 w-8 text-gray-500 dark:text-gray-400 md:h-8 md:w-8"
            >
              {theme === "dark" ? (
                <HiSun className="h-4 w-4" />
              ) : (
                <HiMoon className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </Navbar>
  );
};

export default ExampleNavbar;
